/** The minimal shape `qualitativeVariantLabels` needs — generic so callers (including
 * this module's own tests) can exercise the ranking/bucketing logic against synthetic
 * fixtures without having to satisfy the full, id-union-narrowed `QuoteVariant` shape. */
export interface RankableVariant {
  readonly id: string
  readonly leadTimeDaysMax: number
}

/**
 * UID-622 (cart follow-up, ADR-0181 D3 OPEN-note addendum) — the qualitative
 * delivery-speed/cost label shown under a delivery-option row's price, on BOTH
 * `QuoteVariantSelector.tsx` (RFQ "Choose a quote option") and
 * `CartQuoteOptionSelector.tsx` (cart "Choose how this order ships"). The
 * "Fastest"/"Cost-effective" copy had been SUPPRESSED on the RFQ surface since
 * UID-434/UID-440 because — until ADR-0181 D3 repriced `prefer_us_blend` — the
 * North America ladder was not monotone in lead-time/price, and the label would
 * have misstated the actual relationship. ADR-0181 D3 fixed the ladder, and this
 * ticket is the direct product decision to re-enable the copy, resolving that
 * ADR's "OPEN, routed to PM" note. Extracted here, in the shared RFQ vocabulary
 * module, so both surfaces rank identically and can never disagree on which
 * option is "Fastest"/"Standard"/"Cost-effective".
 */
export type QuoteVariantQualitativeLabel = 'Fastest' | 'Standard' | 'Cost-effective'

/**
 * Ranks `variants` by `leadTimeDaysMax` ascending (fastest first) and buckets
 * the ranked list into the three qualitative labels:
 *   - rank 0 (fastest)              -> 'Fastest'
 *   - the last rank (slowest)       -> 'Cost-effective'
 *   - every rank in between        -> 'Standard'
 *
 * This ranks across the WHOLE list passed in — callers that want a single
 * ladder spanning multiple region groups (the cart's North America +
 * Overseas columns) must pass all variants together, never one group at a
 * time, or "Fastest"/"Cost-effective" would be awarded independently within
 * each group instead of across the full option set.
 *
 * Deliberately ranks by `leadTimeDaysMax`, never by `ratio`/price directly —
 * lead time is the axis the labels describe ("Fastest" / slowest), and
 * ADR-0181 D5's monotonicity invariant guarantees price moves in lockstep
 * with it for the real `RFQ_QUOTE_VARIANTS` table, so the two ranking choices
 * agree today. Ranking by lead time rather than price is still the more
 * faithful reading of the label copy and keeps this utility correct even if
 * a future variant broke that lockstep.
 *
 * A 2-item list has no middle rank at all (both endpoints, no 'Standard');
 * a 1-item list is 'Fastest' only (there is no slower option to be
 * 'Cost-effective' against, so no other label is assigned).
 */
export function qualitativeVariantLabels<T extends RankableVariant>(
  variants: readonly T[],
): ReadonlyMap<T['id'], QuoteVariantQualitativeLabel> {
  const ranked = [...variants].sort((a, b) => a.leadTimeDaysMax - b.leadTimeDaysMax)
  const labels = new Map<T['id'], QuoteVariantQualitativeLabel>()

  ranked.forEach((variant, index) => {
    if (index === 0) {
      labels.set(variant.id, 'Fastest')
      return
    }
    if (index === ranked.length - 1) {
      labels.set(variant.id, 'Cost-effective')
      return
    }
    labels.set(variant.id, 'Standard')
  })

  return labels
}
